import { REQUEST_TIMEOUT_MS } from '@ctcells/core'
import type { OrchestratorSchemas } from '@ctcells/contract'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'
import type {
  ModulePoseReading,
  ModulePosition,
  PositionRegistryItem,
  PositionRegistryWrite,
} from '../queries/positions.ts'

/**
 * 위치 등록부 쓰기 · 모듈 위치 티칭 — `IFC-RO-96` · `99` · `100` · `101`(v0.9.4).
 *
 * 실물(`ctcells-core/app/views/settings_positions.py` · `views/modules.py` 「코어 티칭」 절 · core@d50b8cf4)
 *
 * | 연산 | 경로 | 역할 · 락 | 본문 | 응답 |
 * | --- | --- | --- | --- | --- |
 * | 지점 등록 | `POST /settings/positions` | operator · admin · 락 없음 | `command_id` 필수 + `position_id` · 칸들 | 201 `PositionRegistryItem` |
 * | 지점 수정 · 비활성 · 재활성 | `PUT /settings/positions/{pid}` | operator · admin · 락 없음 | `command_id` 필수 + **든 칸만**(병합 · `values_of(body, current)`) | 200 `PositionRegistryItem` |
 * | 조그 한 걸음 | `POST /modules/{id}/jog` | operator · admin · **제어락** · 티칭 가드 | 모듈 `jogAxis` 본문 그대로(`robot_id` · `axis` · `delta` · `speed_pct?`) | 200 움직인 뒤 `ModulePose` |
 * | 지점 티칭 | `PUT /modules/{id}/positions/{pid}` | operator · admin · **제어락** · 티칭 가드 | `pose` · `reason`(필수) · `approach?` · `note?` | 200 모듈 `Position` |
 * | 슬라이드 mm 티칭 | `PUT /modules/{id}/positions/{pid}/slide-mm` | 같다 | `slide_mm` · `reason`(필수) · `note?` | 200 모듈 `Position` |
 *
 * - 🔴 등록부 PUT 은 **병합**이다(본문에 없는 칸은 지금 값을 둔다) — 화면은 **받은 것과 달라진 칸만** 싣는다(CLAUDE.md PUT 절).
 *   거절: 본문 · 정합 위반 422 `REQUEST_VALIDATION_FAIL`(`context.reason` = 서버 문장) · 보호 · 사용 중 · 이미 있는 이름 409
 *   `POSITION_REGISTRY_REJECTED`(`context.reason` = `protected` | `in_use` | `id_taken` + `why` · `recipe_ids`).
 * - 🔴 티칭 쓰기 둘은 **메모 한 칸(`note`)을 함께 쓴다** — `note` 없이 쓰면 남긴 메모가 `null` 로 지워진다(모듈 계약 `Position.note`).
 *   화면은 그 행의 지금 메모를 그대로 싣는다(`teachBody` · `slideBody`).
 * - 🔴 가드가 닫히면 409 `TEACH_NOT_ALLOWED`(`context.reasons`) · 모듈 거절(409 · 422 · 404)은 **그대로** 온다.
 * - ⚠ 셋 다 장비 좌표를 바꾸거나 장비를 움직인다 — 낙관적 갱신 없음. 응답 · 무효화로만 그린다.
 * - 제어락 헤더는 `controlToken.ts` 경로표가 붙인다(세 경로가 거기 있어야 한다 — 없으면 헤더 없이 나가 409 다).
 */
export type TeachPositionWrite = OrchestratorSchemas['schemas']['TeachPositionWrite']
export type TeachSlideWrite = OrchestratorSchemas['schemas']['TeachSlideWrite']
/** 설치 정의 쓰기 본문(`IFC-RO-127`) — 항목 셋: 템플릿 id 글자 · `{labware_id, well_point_offsets?}` · `{definitionUri, well_point_offsets}` */
export type InstalledLabwareWrite = OrchestratorSchemas['schemas']['InstalledLabwareWrite']
export type InstalledLabwareItem = InstalledLabwareWrite['labware_ids'][number]
export type WellPointOffsets = OrchestratorSchemas['schemas']['WellPointOffsets']

const REGISTRY = '/settings/positions'

/** 등록부 쓰기 본문에서 멱등키를 뺀 칸 — 훅이 `command_id` 를 짓는다 */
export type PositionRegistryFields = Omit<PositionRegistryWrite, 'command_id'>

export const useCreatePositionEntry = createCommand<
  { fields: PositionRegistryFields; commandId?: string },
  PositionRegistryItem
>({
  send: ({ fields, commandId }) =>
    orchestrator.request<PositionRegistryItem>({
      method: 'POST',
      path: REGISTRY,
      body: { command_id: commandId ?? newCommandId(), ...fields },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.positions.registry()],
})

export const useUpdatePositionEntry = createCommand<
  { positionId: string; fields: PositionRegistryFields; commandId?: string },
  PositionRegistryItem
>({
  send: ({ positionId, fields, commandId }) =>
    orchestrator.request<PositionRegistryItem>({
      method: 'PUT',
      path: `${REGISTRY}/${encodeURIComponent(positionId)}`,
      body: { command_id: commandId ?? newCommandId(), ...fields },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.positions.registry()],
})

/** 조그 본문 — 모듈 계약 `jogAxis` 그대로(`command_id` 는 훅이 짓는다) */
export interface JogStep {
  /** `capabilities.robots[].robot_id` 또는 `slide`(트레이 슬라이더) */
  robot_id: string
  axis: 'x' | 'y' | 'z' | 'rx' | 'ry' | 'rz' | 'lift' | 'traverse' | 'slide'
  /** 한 걸음 — 직선 mm · 회전 rad · 0 이면 모듈이 422 */
  delta: number
  speed_pct?: number
}

export const useJogModule = createCommand<
  { moduleId: string; step: JogStep; commandId?: string },
  ModulePoseReading
>({
  send: ({ moduleId, step, commandId }) =>
    orchestrator.request<ModulePoseReading>({
      method: 'POST',
      path: `/modules/${encodeURIComponent(moduleId)}/jog`,
      body: { command_id: commandId ?? newCommandId(), ...step },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  /* 응답이 곧 움직인 뒤 자세다 — 화면은 그 값을 그린다. 다음 진입 때를 위해 자세 캐시만 비운다 */
  invalidates: ({ moduleId }) => [keys.positions.pose(moduleId)],
})

export const useTeachModulePosition = createCommand<
  {
    moduleId: string
    positionId: string
    fields: Omit<TeachPositionWrite, 'command_id'>
    commandId?: string
  },
  ModulePosition
>({
  send: ({ moduleId, positionId, fields, commandId }) =>
    orchestrator.request<ModulePosition>({
      method: 'PUT',
      path: `/modules/${encodeURIComponent(moduleId)}/positions/${encodeURIComponent(positionId)}`,
      body: { command_id: commandId ?? newCommandId(), ...fields },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  /* 등록부의 `taught` 칸도 그 값에서 온다(`position_snapshot`) — 둘 다 비운다 */
  invalidates: ({ moduleId }) => [keys.positions.moduleAll(moduleId), keys.positions.registry()],
})

export const useTeachModuleSlide = createCommand<
  {
    moduleId: string
    positionId: string
    fields: Omit<TeachSlideWrite, 'command_id'>
    commandId?: string
  },
  ModulePosition
>({
  send: ({ moduleId, positionId, fields, commandId }) =>
    orchestrator.request<ModulePosition>({
      method: 'PUT',
      path: `/modules/${encodeURIComponent(moduleId)}/positions/${encodeURIComponent(
        positionId,
      )}/slide-mm`,
      body: { command_id: commandId ?? newCommandId(), ...fields },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: ({ moduleId }) => [keys.positions.moduleAll(moduleId), keys.positions.registry()],
})

/**
 * 설치 정의 쓰기 — `PUT /modules/{id}/positions/{pid}/installed-labware`(`IFC-RO-127` · `views/modules.py`
 * `set_module_installed_labware` · operator · admin · **제어락** · 직접 입력 가드).
 *
 * 🔴 **통째로 바꾼다**(모듈 `set_installed_labware` 「설치 정의 목록을 통째로 바꾼다(빈 목록 = 걷음)」) — 목록에 없는 항목은 걷힌다.
 *    화면은 받은 목록 위에 고친 것만 얹어 보낸다(`shell/installedLabwareModel.ts` `installedLabwareItems`).
 * - 가드: 그 모듈이 배정된 끝나지 않은 작업이 있으면(`batch_open` · 시작 전 작업 포함) · 제어 주체가 코어가 아니면 ·
 *   상태 미러가 낡았거나 모듈이 일하면 409 `TEACH_NOT_ALLOWED`(`context.reasons`). 화면은 서버 거절을 그대로 적는다
 * - 모양 · 모르는 템플릿 · 같은 `definitionUri` 둘 · z ≠ 0 은 422(모듈 호출 0) · 버전이 어긋나면 409 `POSITION_VERSION_CONFLICT`
 */
export const useSetInstalledLabware = createCommand<
  {
    moduleId: string
    positionId: string
    fields: Omit<InstalledLabwareWrite, 'command_id'>
    commandId?: string
  },
  ModulePosition
>({
  send: ({ moduleId, positionId, fields, commandId }) =>
    orchestrator.request<ModulePosition>({
      method: 'PUT',
      path: `/modules/${encodeURIComponent(moduleId)}/positions/${encodeURIComponent(
        positionId,
      )}/installed-labware`,
      body: { command_id: commandId ?? newCommandId(), ...fields },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  /* 그 자리의 설치 정의 · 버전이 바뀐다 — 목록을 비운다(등록부 칸은 설치 정의를 싣지 않는다) */
  invalidates: ({ moduleId }) => [keys.positions.moduleAll(moduleId)],
})
